import useSWR from 'swr'
import { auditsApi, type AuditsApiError } from '../api/index.ts'
import { LIVE_STATES, type AuditDetail, type AuditSummary } from '../types.ts'
import { auditKeys } from './audits.keys.ts'

function unwrap<T>(
  result: { success: true; data: T } | { success: false; error: AuditsApiError },
): T {
  if (result.success) return result.data
  throw result.error
}

export function useAudits() {
  const { data, error, isLoading, mutate } = useSWR<AuditSummary[], AuditsApiError>(
    auditKeys.list,
    async () => unwrap(await auditsApi.list()),
  )
  return { audits: data ?? [], error: error ?? null, isLoading, refresh: mutate }
}

/**
 * One audit, polled every 3 s **only** while it is `snapshotting` or
 * `processing` (§10.1) — the conditional-polling posture `useSyncRun` set.
 */
export function useAudit(auditId: string | undefined) {
  const { data, error, isLoading, mutate } = useSWR<AuditDetail, AuditsApiError>(
    auditId ? auditKeys.detail(auditId) : null,
    async () => unwrap(await auditsApi.get(auditId!)),
    { refreshInterval: (latest) => (latest && isLive(latest.audit.state) ? 3000 : 0) },
  )
  return { detail: data ?? null, error: error ?? null, isLoading, refresh: mutate }
}

export function isLive(state: AuditDetail['audit']['state']): boolean {
  return LIVE_STATES.includes(state)
}

export function useAuditStages(auditId: string | undefined, live: boolean) {
  const { data, error, mutate } = useSWR(
    auditId ? auditKeys.stages(auditId) : null,
    async () => unwrap(await auditsApi.stages(auditId!)),
    { refreshInterval: live ? 3000 : 0 },
  )
  return { stages: data ?? [], error: error ?? null, refresh: mutate }
}

export function useAuditAnomalies(auditId: string | undefined, live: boolean) {
  const { data, mutate } = useSWR(
    auditId ? auditKeys.reviews(auditId) : null,
    async () => unwrap(await auditsApi.openReviews(auditId!)),
    { refreshInterval: live ? 3000 : 0 },
  )
  const byKind = new Map<string, string[]>()
  for (const item of data ?? []) byKind.set(item.kind, [...(byKind.get(item.kind) ?? []), item.id])
  return { byKind, total: data?.length ?? 0, refresh: mutate }
}

export function useAuditPipelines() {
  const { data } = useSWR(auditKeys.pipelines, async () => unwrap(await auditsApi.pipelines()))
  return data ?? []
}

/** A draft's uncovered pipeline: polled while its import runs. */
export function useDraftBackfill(pipelineId: string) {
  const { data } = useSWR(
    auditKeys.backfill(pipelineId),
    async () => unwrap(await auditsApi.backfill(pipelineId)),
    {
      refreshInterval: (latest) =>
        latest?.job && (latest.job.state === 'queued' || latest.job.state === 'running') ? 3000 : 0,
    },
  )
  return data ?? null
}
